import React, {ChangeEvent} from 'react';
import MuiSwitch, {SwitchProps as MuiSwitchProps} from '@mui/material/Switch';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import {
   FormControlLabel as MuiFormControlLabel,
   FormControlLabelProps,
   Grid,
   Typography,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {TFunctionResult} from 'i18next';
import {useController} from 'react-hook-form';
import {useRegisterLabel} from 'common/context/FormContext';
import {useEnsureFieldValue} from 'common/hooks/useEnsureFieldValue';

const FormControlLabel = withStyles({
   label: {
      marginRight: '2rem',
   },
})(MuiFormControlLabel);

type Props = {
   label?: FormControlLabelProps['label'] | TFunctionResult;
   labels?: [string, string];
   name: string;
   disabled?: boolean;
   labelPlacement?: FormControlLabelProps['labelPlacement'];
   title?: string;
   silent?: boolean;
};

interface SwitchProps extends Props {
   value?: MuiSwitchProps['value'];
   checked: MuiSwitchProps['checked'];
   onChange: MuiSwitchProps['onChange'];
}
export const Switch = ({
   label,
   labels,
   name,
   disabled,
   labelPlacement = 'start',
   title,
   value,
   checked,
   onChange,
}: SwitchProps) => {
   const readOnly = React.useContext(readOnlyContext);

   return (
      <FormControlLabel
         label={label as string}
         labelPlacement={labelPlacement}
         disabled={disabled || readOnly}
         control={
            <Typography component="div">
               <Grid component="label" container alignItems="center" title={title} spacing={1}>
                  <Grid item>{labels?.[0]}</Grid>
                  <Grid item>
                     <MuiSwitch
                        disabled={disabled || readOnly}
                        name={name}
                        value={value}
                        checked={checked}
                        onChange={onChange}
                     />
                  </Grid>
                  <Grid item>{labels?.[1]}</Grid>
               </Grid>
            </Typography>
         }
      />
   );
};

const HookFormSwitch = (props: Props) => {
   const {
      field: {onChange: _onChange, value: checked},
   } = useController({name: props.name});
   useRegisterLabel(props.name, props.label as string);

   const resetField = useEnsureFieldValue();
   const onChange = React.useCallback(
      (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
         if (props.silent) {
            console.debug('Switch silent set', event, checked);
            resetField(props.name, checked);
         } else {
            return _onChange(event, checked);
         }
      },
      [props.name, _onChange, resetField]
   );

   return <Switch {...props} checked={checked} onChange={onChange} />;
};

export default HookFormSwitch;
